import { Component, Input, OnInit } from '@angular/core';
import { DateTime, FixedOffsetZone } from 'luxon';
import { DateService } from '../../../../../services/date/date.service';
import { PtLuxonDate } from '../../../../../models/projects/date.models';
import {
  JackingFormInstanceEditor,
  JackingLegSummaryLegEditor,
  JackingSectionGroupTemplateEditor
} from '../../../../../models/jacking-v2/jacking.models';

type TimeFieldKey =
  | 'legMaxPullingLoadTime'
  | 'legFreeInHolesExtTime'
  | 'clearOfSeaBedExtTime';

type OffsetFieldKey =
  | 'legMaxPullingLoadTimeOffset'
  | 'legFreeInHolesExtTimeOffset'
  | 'clearOfSeaBedExtTimeOffset';

@Component({
  selector: 'jacking-leg-summaries',
  templateUrl: 'jacking-leg-summaries.component.html'
})
export class JackingLegSummariesComponent implements OnInit {
  @Input() editor: JackingFormInstanceEditor;
  @Input() group: JackingSectionGroupTemplateEditor;
  @Input() legsCount = 4;
  @Input() isReadonly = false;
  @Input() dailyLogDate: PtLuxonDate;
  @Input() completedDayByIso: { [isoDate: string]: boolean } = {};
  @Input() activitySpansMultipleDays = false;

  mask: any[] = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];

  // UI-only state (NOT on the model)
  private timeToShowByKey = new Map<string, string>();
  private selectedLocalDateIsoByKey = new Map<string, string>();

  constructor(private dateService: DateService) {}

  ngOnInit() {
    // seed UI strings from stored PtLuxonDate values
    this.rows.forEach(r => {
      this.seedFromModel(r, 'legMaxPullingLoadTime');
      this.seedFromModel(r, 'legFreeInHolesExtTime');
      this.seedFromModel(r, 'clearOfSeaBedExtTime');
    });
  }

  get summary(): any {
    if (!this.editor) { return null; }

    if (!this.editor.jackingLegSummary) {
      this.editor.jackingLegSummary = { id: 0, legs: [] } as any;
    }

    if (!(this.editor.jackingLegSummary as any).legs) {
      (this.editor.jackingLegSummary as any).legs = [];
    }

    (this.editor.jackingLegSummary as any).legs = this.ensureLegRowArray((this.editor.jackingLegSummary as any).legs);
    return this.editor.jackingLegSummary;
  }

  get rows(): JackingLegSummaryLegEditor[] {
    return ((this.summary as any)?.legs || []) as JackingLegSummaryLegEditor[];
  }

  // UI uses same legs list in each block
  maxPullingRows(): JackingLegSummaryLegEditor[] { return this.rows; }
  freeInHolesRows(): JackingLegSummaryLegEditor[] { return this.rows; }
  clearOfSeabedRows(): JackingLegSummaryLegEditor[] { return this.rows; }

  private cellKey(legNumber: number, timeField: TimeFieldKey): string {
    return `${legNumber}:${timeField}`;
  }

  private getTimeField(row: any, timeField: TimeFieldKey): PtLuxonDate {
    return row?.[timeField] as PtLuxonDate;
  }

  private setTimeField(row: any, timeField: TimeFieldKey, value: PtLuxonDate | null) {
    row[timeField] = value as any;
  }

  private getOffsetFieldKey(timeField: TimeFieldKey): OffsetFieldKey {
    switch (timeField) {
      case 'legMaxPullingLoadTime': return 'legMaxPullingLoadTimeOffset';
      case 'legFreeInHolesExtTime': return 'legFreeInHolesExtTimeOffset';
      case 'clearOfSeaBedExtTime': return 'clearOfSeaBedExtTimeOffset';
    }
  }

  // seed ui from model
  private seedFromModel(row: JackingLegSummaryLegEditor, timeField: TimeFieldKey) {
    const key = this.cellKey(row.legNumber, timeField);

    const pt = this.getTimeField(row, timeField);
    if (!pt?.dateTime) {
      this.timeToShowByKey.set(key, '');
      this.selectedLocalDateIsoByKey.set(key, '');
      return;
    }

    const dt = pt.dateTime as any as DateTime;
    this.timeToShowByKey.set(key, dt?.toFormat ? dt.toFormat('HH:mm') : '');
    this.selectedLocalDateIsoByKey.set(key, dt?.toISODate ? (dt.toISODate() || '') : '');
    this.regexChange(this.timeToShowByKey.get(key) || '');
  }

  // Used by template
  getTimeText(row: JackingLegSummaryLegEditor, timeField: TimeFieldKey): string {
    return this.timeToShowByKey.get(this.cellKey(row.legNumber, timeField)) || '';
  }

  // ----------------------------------------------------------
  // Input handlers
  // ----------------------------------------------------------

  regexChange(inputTime: string) {
    if (!inputTime) {
      this.mask[1] = new RegExp('\\d');
      return;
    }

    if (inputTime.charAt(0) === '2') {
      this.mask[1] = new RegExp('[0-3]');
    } else {
      this.mask[1] = new RegExp('\\d');
    }
  }

  async onTimeTextChanged(row: JackingLegSummaryLegEditor, timeField: TimeFieldKey, text: string) {
    if (this.fieldIsReadonly(row, timeField)) { return; }

    const key = this.cellKey(row.legNumber, timeField);
    this.timeToShowByKey.set(key, text || '');
    this.regexChange(text || '');

    await this.applyTimeChange(row, timeField, false);
  }

  async onLegDateChanged(row: JackingLegSummaryLegEditor, timeField: TimeFieldKey, dt: DateTime) {
    if (this.fieldIsReadonly(row, timeField)) { return; }

    const key = this.cellKey(row.legNumber, timeField);
    const iso = dt?.toISODate ? (dt.toISODate() || '') : '';
    this.selectedLocalDateIsoByKey.set(key, iso);

    await this.applyTimeChange(row, timeField, true);
  }

  async onOffsetSelected(row: JackingLegSummaryLegEditor, timeField: TimeFieldKey, pt: PtLuxonDate) {
    if (this.fieldIsReadonly(row, timeField)) { return; }
    if (!pt?.dateTime) { return; }

    const offsetField = this.getOffsetFieldKey(timeField);
    const key = this.cellKey(row.legNumber, timeField);

    this.setTimeField(row, timeField, pt);
    (row as any)[offsetField] = pt.dateTime.offset / 60;

    // keep UI text in sync
    this.timeToShowByKey.set(key, pt.dateTime.toFormat('HH:mm'));
    this.selectedLocalDateIsoByKey.set(key, pt.dateTime.toISODate() || '');
    this.regexChange(this.timeToShowByKey.get(key) || '');

    await this.bumpTimeIfBeforeReference(row, timeField, this.timeToShowByKey.get(key) || '', null, false);
  }

  private async applyTimeChange(row: JackingLegSummaryLegEditor, timeField: TimeFieldKey, dateChanged: boolean) {
    const key = this.cellKey(row.legNumber, timeField);
    const timeToShow = (this.timeToShowByKey.get(key) || '').trim();
    const offsetField = this.getOffsetFieldKey(timeField);
    const originalTimeCopy = this.cloneTimeField(row, timeField);

    const allowed = await this.dateService.timeAllowed(
      timeToShow,
      this.getBaseDateForEntry(timeField, row.legNumber)
    );

    if (!allowed || !timeToShow) {
      if (!timeToShow) {
        // clear model value (stay clean)
        const empty = new PtLuxonDate(null, true);
        this.setTimeField(row, timeField, empty);
        (row as any)[offsetField] = null;
      }
      return;
    }

    const baseDate = this.getBaseDateForEntry(timeField, row.legNumber);
    const pt = await this.dateService.setPtLuxonDateFromUserEnteredString(timeToShow, baseDate);

    if ((pt as any)?.offsetNull?.()) { return; }
    if ((pt as any)?.isLostInTime?.result) { return; }
    if (!pt?.dateTime) { return; }

    // keep LOCAL for UI so offset is correct immediately
    this.setTimeField(row, timeField, pt);
    (row as any)[offsetField] = pt.dateTime.offset / 60;

    await this.bumpTimeIfBeforeReference(row, timeField, timeToShow, originalTimeCopy, dateChanged);

    // update UI-selected date for multi-day
    const currentPt = this.getTimeField(row, timeField);
    this.selectedLocalDateIsoByKey.set(key, currentPt?.dateTime?.toISODate() || '');
  }

  private getBaseDateForEntry(timeField: TimeFieldKey, legNumber: number): DateTime {
    // Default local day = operation start local day (never computer time)
    const startLocal = this.editor?.startDate?.dateTime
      ? this.editor.startDate.dateTime.plus({ minutes: this.editor.startOffset || 0 })
      : DateTime.fromObject({ year: 2000, month: 1, day: 1, hour: 0, minute: 0 }, { zone: 'utc' });

    let localDateIso = startLocal.toISODate();

    // If multi-day, use selected date for THIS cell, else the operation start local date
    if (this.activitySpansMultipleDays) {
      const key = this.cellKey(legNumber, timeField);
      const selected = this.selectedLocalDateIsoByKey.get(key) || '';
      localDateIso = selected || localDateIso;

      const currentDailyIso = this.dailyLogDate?.dateTime?.toISODate?.() || '';
      if (currentDailyIso && localDateIso && currentDailyIso > localDateIso) {
        localDateIso = currentDailyIso;
      }
    }

    const [y, m, d] = localDateIso.split('-').map(x => Number(x));
    return DateTime.fromObject({ year: y, month: m, day: d, hour: 0, minute: 0 }, { zone: 'utc' });
  }

  getPickerMinDate(): DateTime | null {
    const startBound = this.rebuildLocalBound(this.editor?.startDate, this.editor?.startOffset ?? null);
    const currentDay = this.dailyLogDate?.dateTime || null;

    if (!currentDay) {
      return startBound;
    }

    if (!startBound) {
      return currentDay;
    }

    return currentDay > startBound ? currentDay : startBound;
  }

  getPickerMaxDate(): DateTime | null {
    const candidates: DateTime[] = [];
    const endBound = this.rebuildLocalBound(this.editor?.endDate, this.editor?.endOffset ?? this.editor?.startOffset ?? null);

    if (endBound) {
      candidates.push(endBound);
    }

    for (const row of this.rows) {
      for (const field of ['legMaxPullingLoadTime', 'legFreeInHolesExtTime', 'clearOfSeaBedExtTime'] as TimeFieldKey[]) {
        const dt = this.getTimeField(row as any, field)?.dateTime || null;
        if (dt) {
          candidates.push(dt);
        }
      }
    }

    if (!candidates.length) {
      return null;
    }

    return candidates.reduce((best, current) => current > best ? current : best, candidates[0]);
  }

  fieldIsReadonly(row: JackingLegSummaryLegEditor, timeField: TimeFieldKey): boolean {
    if (this.isReadonly) {
      return true;
    }

    const currentIsoDate = this.dailyLogDate?.dateTime?.toISODate?.() || '';
    const fieldIsoDate = this.getTimeField(row, timeField)?.dateTime?.toISODate?.() || '';

    return !!currentIsoDate
      && !!fieldIsoDate
      && fieldIsoDate < currentIsoDate
      && !!this.completedDayByIso?.[fieldIsoDate];
  }

  private cloneTimeField(row: JackingLegSummaryLegEditor, timeField: TimeFieldKey): PtLuxonDate | null {
    const current = this.getTimeField(row, timeField);
    if (!current?.dateTime) {
      return null;
    }

    return PtLuxonDate.assignPtLuxonDate({
      dateTime: current.dateTime
    } as any);
  }

  private async bumpTimeIfBeforeReference(
    row: JackingLegSummaryLegEditor,
    timeField: TimeFieldKey,
    timeToShow: string,
    originalTimeCopy: PtLuxonDate | null,
    dateChanged: boolean
  ): Promise<void> {
    if (!this.activitySpansMultipleDays || !timeToShow) {
      return;
    }

    if (dateChanged) {
      return;
    }

    const currentPt = this.getTimeField(row, timeField);
    const currentDt = currentPt?.dateTime || null;
    if (!currentDt) {
      return;
    }

    if (originalTimeCopy?.dateTime) {
      const sameDay = await this.dateService.compareDayOffsetsPreApplied(currentDt, originalTimeCopy.dateTime);
      if (!sameDay) {
        return;
      }
    }

    const operationStartDt = this.editor?.startDate?.dateTime || null;
    if (!operationStartDt) {
      return;
    }

    // Only auto-roll from the operation start day to the next day once.
    // If the cell is already on a later date, repeated edits in the same input
    // should not keep incrementing it further.
    const onOperationStartDay = await this.dateService.compareDayOffsetsPreApplied(currentDt, operationStartDt);
    if (!onOperationStartDay) {
      return;
    }

    const [hoursText, minutesText] = timeToShow.split(':');
    const enteredMinutes = (Number(hoursText) * 60) + Number(minutesText);
    const startMinutes = (operationStartDt.hour * 60) + operationStartDt.minute;

    if (!Number.isFinite(enteredMinutes) || enteredMinutes >= startMinutes) {
      return;
    }

    const bumpedRaw = await this.dateService.incrementPtLuxonDateFromUserEnteredString(timeToShow, currentDt);
    const bumpedPt = PtLuxonDate.assignPtLuxonDate(bumpedRaw as any);
    if (!bumpedPt?.dateTime) {
      return;
    }

    this.setTimeField(row, timeField, bumpedPt);
    const offsetField = this.getOffsetFieldKey(timeField);
    (row as any)[offsetField] = bumpedPt.dateTime.offset / 60;

    const key = this.cellKey(row.legNumber, timeField);
    this.selectedLocalDateIsoByKey.set(key, bumpedPt.dateTime.toISODate() || '');
  }

  private rebuildLocalBound(valueUtc: PtLuxonDate | null | undefined, offsetHours: number | null): DateTime | null {
    const dtUtc = valueUtc?.dateTime?.toUTC?.() ? valueUtc.dateTime.toUTC() : null;
    if (!dtUtc?.isValid) {
      return null;
    }

    if (offsetHours == null) {
      return dtUtc;
    }

    return dtUtc.setZone(FixedOffsetZone.instance(offsetHours * 60));
  }

  // -----------------------------
  // Leg seeding
  // -----------------------------

  private get legs(): number[] {
    const n = this.legsCount || 4;
    return Array.from({ length: n }, (_, i) => i + 1);
  }

  private ensureLegRowArray(arr: any[]) {
    const safe = arr || [];
    const out: any[] = [];

    for (const leg of this.legs) {
      const existing = safe.find(x => x.legNumber === leg);
      out.push(existing || this.newLeg(leg));
    }

    return out;
  }

  private newLeg(legNumber: number): JackingLegSummaryLegEditor {
    return {
      id: 0,
      legNumber,

      legMaxPullingLoad: null,
      legMaxPullingLoadTime: new PtLuxonDate(null, true) as any,
      legMaxPullingLoadTimeOffset: null,

      legFreeInHolesExt: null,
      legFreeInHolesExtTime: new PtLuxonDate(null, true) as any,
      legFreeInHolesExtTimeOffset: null,

      clearOfSeaBedExt: null,
      clearOfSeaBedExtTime: new PtLuxonDate(null, true) as any,
      clearOfSeaBedExtTimeOffset: null
    } as any;
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/components/jacking-leg-summaries/jacking-leg-summaries.component.ts